// Pure helpers for the Activity tab: shift-line formatting, day grouping, filtering and
// row-label derivation. Adapted from the Load Balancer prototype (PR #6802) ActivityList.
import type {
  ScheduleDayType,
  ScheduleMoveKindType,
  ScheduleMoveSourceFilterType,
  ScheduleMoveType,
  ScheduleShiftType,
  ScheduleShiftTypeSectionType,
} from '@load-balancing/types';

export type ScheduleMoveDayGroupType = {
  label: string;
  moves: ScheduleMoveType[];
};

const MOVE_KIND_LABELS: Record<ScheduleMoveKindType, string> = {
  correction: 'Corrected',
  assignment: 'Assigned',
  reassignment: 'Reassigned',
  removal: 'Unassigned',
  shift_cancellation: 'Deleted',
  shift_addition: 'Added',
};

const MOVE_KIND_REJECTED_LABELS: Record<ScheduleMoveKindType, string> = {
  correction: 'Rejected Correction',
  assignment: 'Rejected Assignment',
  reassignment: 'Rejected Reassignment',
  removal: 'Rejected Removal',
  shift_cancellation: 'Rejected Cancellation',
  shift_addition: 'Rejected Addition',
};

/** Move kinds with no selectable grid counterpart (used as CardGroup disabledKeys). */
const GRIDLESS_MOVE_KINDS: ScheduleMoveKindType[] = [
  'correction',
  'shift_cancellation',
];

/**
 * Precomputes a display line per shift id ("Wed May 13 ∙ 07:00–19:00 ∙ RN") across all
 * weeks, so rows can resolve From/To lines with a plain lookup.
 */
export function buildShiftLineMap(
  shiftsByWeek: Record<string, ScheduleShiftType[]>,
  daysByWeek: Record<string, ScheduleDayType[]>,
  shiftTypeSections: ScheduleShiftTypeSectionType[]
): Record<string, string> {
  const timeBySection = Object.fromEntries(
    shiftTypeSections.map((section) => [section.key, section.time])
  );

  return Object.fromEntries(
    Object.entries(shiftsByWeek).flatMap(([weekKey, shifts]) => {
      const dayByKey = Object.fromEntries(
        (daysByWeek[weekKey] ?? []).map((day) => [day.key, day])
      );

      return shifts.map((shift) => {
        const day = dayByKey[shift.dayKey];
        const dayPart = day ? `${day.dayLabel} ${day.dateLabel}` : shift.dayKey;
        const timePart = timeBySection[shift.shiftTypeSection] ?? '';

        return [
          shift.id,
          [dayPart, timePart, shift.role].filter(Boolean).join(' ∙ '),
        ];
      });
    })
  );
}

export function getMoveTypeLabel(move: ScheduleMoveType): string {
  return move.status === 'rejected'
    ? MOVE_KIND_REJECTED_LABELS[move.kind]
    : MOVE_KIND_LABELS[move.kind];
}

export function getMoveSubject(move: ScheduleMoveType): string {
  if (move.kind === 'shift_addition') return 'Open shift';
  if (move.kind === 'shift_cancellation') {
    return move.workerName ?? 'Open shift';
  }
  return move.workerName ?? '';
}

export type ScheduleMoveShiftLinesType = {
  primary: string | null;
  secondary: string | null;
};

/**
 * Resolves the shift display line(s) for a move: reassignments show From (secondary) and
 * To (primary); origin-only kinds show the origin; destination kinds show the destination.
 */
export function getMoveShiftLines(
  move: ScheduleMoveType,
  shiftLineById: Record<string, string>
): ScheduleMoveShiftLinesType {
  const fromLine = move.fromShiftId
    ? (shiftLineById[move.fromShiftId] ?? null)
    : null;
  const toLine = move.toShiftId
    ? (shiftLineById[move.toShiftId] ?? null)
    : null;

  if (move.kind === 'reassignment') {
    return { primary: toLine, secondary: fromLine };
  }
  if (move.kind === 'assignment' || move.kind === 'shift_addition') {
    return { primary: toLine, secondary: null };
  }
  return { primary: fromLine, secondary: null };
}

/** "Today", "Yesterday" or "Wed 6/3". */
export function getDayLabel(isoTimestamp: string, now = new Date()): string {
  const date = new Date(isoTimestamp);
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  if (date.toDateString() === now.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';

  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  return `${weekday} ${date.getMonth() + 1}/${date.getDate()}`;
}

/** "Alex Morgan 05/13/2026 at 11:05am PDT" — actor omitted for system moves. */
export function formatMoveTimestamp(
  isoTimestamp: string,
  actor?: string
): string {
  const date = new Date(isoTimestamp);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const time = date
    .toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    })
    .replace(/\s?(AM|PM)\s?/, (match) => `${match.trim().toLowerCase()} `);
  const stamp = `${month}/${day}/${date.getFullYear()} at ${time}`;

  return actor ? `${actor} ${stamp}` : stamp;
}

export function sortMovesNewestFirst(
  moves: ScheduleMoveType[]
): ScheduleMoveType[] {
  return [...moves].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** Groups consecutive moves by day label — input must already be sorted newest-first. */
export function groupMovesByDay(
  moves: ScheduleMoveType[],
  now = new Date()
): ScheduleMoveDayGroupType[] {
  return moves.reduce<ScheduleMoveDayGroupType[]>((groups, move) => {
    const label = getDayLabel(move.createdAt, now);
    const lastGroup = groups[groups.length - 1];

    if (lastGroup && lastGroup.label === label) {
      lastGroup.moves.push(move);
      return groups;
    }
    return [...groups, { label, moves: [move] }];
  }, []);
}

export function filterMovesBySource(
  moves: ScheduleMoveType[],
  sourceFilter: ScheduleMoveSourceFilterType
): ScheduleMoveType[] {
  if (sourceFilter === 'all') return moves;
  return moves.filter((move) => move.source === sourceFilter);
}

export function filterMovesByStaff(
  moves: ScheduleMoveType[],
  query: string
): ScheduleMoveType[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return moves;

  return moves.filter((move) =>
    (move.workerName ?? '').toLowerCase().includes(normalizedQuery)
  );
}

/** Ids of moves with no grid counterpart — fed to CardGroup's disabledKeys. */
export function getGridlessMoveIds(moves: ScheduleMoveType[]): string[] {
  return moves
    .filter((move) => GRIDLESS_MOVE_KINDS.includes(move.kind))
    .map((move) => move.id);
}
